import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Crown, HeartHandshake, Scale, Megaphone, Trash2 } from "lucide-react";
import { clubInfo } from "@/lib/mbti";

const ROLE_META = {
  president: { label: "Consultor Presidente da Liga", icon: Crown, color: "#EAB308" },
  coach: { label: "Secretário do Clube", icon: HeartHandshake, color: "#22C55E" },
  administrator: { label: "Presidente do Clube", icon: Scale, color: "#3B82F6" },
};

/* Voz da Liderança: mensagem da semana do Presidente da Liga (para todos) e do
   Secretário / Presidente de cada clube (para o seu clube). */
export default function ClubVoice() {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/club-voice").then((r) => {
      setData(r.data);
      setText(r.data.my_message?.text || "");
    }).catch(() => setData(null));
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/club-voice", { text });
      toast.success("Mensagem da semana publicada");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/club-voice/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return null;
  if (data.items.length === 0 && !data.can_post) return null;

  return (
    <div className="kw-card p-6" data-testid="club-voice">
      <div className="flex items-center justify-between gap-3 mb-5 border-b border-[#222] pb-4">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6 text-[#EAB308]" />
          <div>
            <div className="kw-overline">Semana {data.week || "—"}</div>
            <h2 className="font-display text-2xl font-black uppercase">Voz da Liderança</h2>
          </div>
        </div>
        {data.can_post && (
          <button className="kw-btn-secondary !py-2 text-[11px]" onClick={() => setOpen(!open)}
            data-testid="club-voice-compose">
            {data.my_message ? "Editar a minha mensagem" : "Deixar mensagem"}
          </button>
        )}
      </div>

      {open && (
        <div className="mb-5 space-y-3" data-testid="club-voice-form">
          <textarea className="kw-input min-h-[110px]" value={text} maxLength={1200}
            placeholder="O que queres dizer ao teu clube esta semana? Uma ideia, um reconhecimento, um foco."
            onChange={(e) => setText(e.target.value)} data-testid="club-voice-input" />
          <div className="flex items-center gap-3">
            <button className="kw-btn-primary !py-2" onClick={save} disabled={saving || text.trim().length < 10}
              data-testid="club-voice-save">{saving ? "A publicar…" : "Publicar"}</button>
            <span className="text-[10px] uppercase tracking-widest text-[#555]">{text.length}/1200</span>
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <p className="text-sm text-[#737373]" data-testid="club-voice-empty">
          Ainda sem mensagens esta semana.
        </p>
      ) : (
        <div className="space-y-4">
          {data.items.map((m) => {
            const meta = ROLE_META[m.role] || ROLE_META.coach;
            const info = m.club ? clubInfo(m.club) : null;
            const accent = m.role === "president" ? "#EAB308" : info?.color || meta.color;
            return (
              <div key={m.id} className="border-l-2 pl-4 py-1" style={{ borderColor: accent }}
                data-testid={`club-voice-item-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <meta.icon className="w-3.5 h-3.5" style={{ color: accent }} />
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>
                    {meta.label}{m.club ? ` · ${m.club}` : " · toda a Liga"}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#737373]">{m.author_name}</span>
                  {m.can_edit && (
                    <button onClick={() => remove(m.id)} className="text-[#555] hover:text-[#E50000] ml-auto"
                      data-testid={`club-voice-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <p className="text-sm text-[#D4D4D4] mt-2 whitespace-pre-wrap leading-relaxed">{m.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
